import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wand2, Trash2 } from "lucide-react";

/**
 * VariantBuilder
 * Props:
 *  - attributes: full list of master attributes (from /admin/attributes)
 *  - selectedIds: list of attribute ids in use on this product
 *  - variants: list of { id, sku, attributes: {attr_id: value_id}, price, stock, image_url }
 *  - basePrice: fallback price when generating new variants
 *  - onChange({ selectedIds, variants })
 */
export default function VariantBuilder({ attributes, selectedIds, variants, basePrice = 0, onChange }) {
    const [chosen, setChosen] = useState(selectedIds || []);
    const [rows, setRows] = useState(variants || []);

    useEffect(() => { setChosen(selectedIds || []); }, [selectedIds]);
    useEffect(() => { setRows(variants || []); }, [variants]);

    const emit = (next) => onChange?.(next);
    const propagate = (nextChosen, nextRows) => emit({ selectedIds: nextChosen, variants: nextRows });

    const toggleAttr = (id) => {
        const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
        setChosen(next);
        propagate(next, rows);
    };

    const cartesian = (arrays) => arrays.reduce(
        (acc, cur) => acc.flatMap((a) => cur.map((b) => [...a, b])),
        [[]],
    );

    const generate = () => {
        const attrDocs = chosen.map((id) => attributes.find((a) => a.id === id)).filter(Boolean);
        if (attrDocs.length === 0) {
            setRows([]);
            propagate(chosen, []);
            return;
        }
        const valueLists = attrDocs.map((a) => a.values || []);
        const combos = cartesian(valueLists);
        const next = combos.map((combo) => {
            const attrMap = {};
            attrDocs.forEach((a, i) => { attrMap[a.id] = combo[i].id; });
            // try to preserve existing variant by attribute combination
            const matchKey = JSON.stringify(attrMap);
            const existing = rows.find((r) => JSON.stringify(r.attributes || {}) === matchKey);
            const labelParts = combo.map((v) => v.value).join(" / ");
            return {
                id: existing?.id || `var-${Math.random().toString(36).slice(2, 10)}`,
                sku: existing?.sku || labelParts.replace(/\W+/g, "-").toLowerCase(),
                attributes: attrMap,
                price: existing?.price ?? Number(basePrice) ?? 0,
                stock: existing?.stock ?? 0,
                image_url: existing?.image_url || "",
            };
        });
        setRows(next);
        propagate(chosen, next);
    };

    const updateRow = (idx, patch) => {
        const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
        setRows(next);
        propagate(chosen, next);
    };
    const removeRow = (idx) => {
        const next = rows.filter((_, i) => i !== idx);
        setRows(next);
        propagate(chosen, next);
    };

    const labelFor = (attrMap) => {
        return Object.entries(attrMap || {}).map(([attrId, valueId]) => {
            const a = attributes.find((x) => x.id === attrId);
            const v = a?.values?.find((x) => x.id === valueId);
            return v ? `${a?.name || ""}: ${v.value}` : "";
        }).filter(Boolean).join(" / ");
    };

    const usableAttrs = useMemo(() => attributes || [], [attributes]);

    return (
        <div className="space-y-4">
            <div>
                <Label className="mb-2 block">Variation attributes</Label>
                {usableAttrs.length === 0 ? (
                    <p className="text-sm text-ink-700">Define attributes (Color, Size…) under the Attributes tab to enable product variations.</p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {usableAttrs.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => toggleAttr(a.id)}
                                className={`rounded-full px-3 h-9 text-sm border-2 transition ${chosen.includes(a.id) ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-ink-700 hover:border-brand-300"}`}
                                data-testid={`variant-attr-toggle-${a.id}`}
                            >
                                {a.name} <span className="text-[10px] opacity-70">({a.values?.length || 0})</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {chosen.length > 0 && (
                <div className="flex justify-between items-center">
                    <p className="text-xs text-ink-700">
                        {rows.length === 0
                            ? "Click \"Generate\" to create one variant per combination."
                            : `${rows.length} variant${rows.length === 1 ? "" : "s"} configured.`}
                    </p>
                    <Button type="button" onClick={generate} className="rounded-full bg-accent-500 hover:bg-accent-600 text-white shadow-cta" data-testid="variant-generate-btn">
                        <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Generate / refresh
                    </Button>
                </div>
            )}

            {rows.length > 0 && (
                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-paper-alt text-xs uppercase tracking-wider text-ink-700">
                            <tr>
                                <th className="text-left px-3 py-2">Variant</th>
                                <th className="text-left px-3 py-2">SKU</th>
                                <th className="text-right px-3 py-2">Price (₹)</th>
                                <th className="text-right px-3 py-2">Stock</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.id} className="border-t border-gray-100" data-testid={`variant-row-${i}`}>
                                    <td className="px-3 py-2 text-xs">{labelFor(r.attributes)}</td>
                                    <td className="px-3 py-2"><Input value={r.sku || ""} onChange={(e) => updateRow(i, { sku: e.target.value })} className="rounded-lg h-8 text-xs" data-testid={`variant-sku-${i}`} /></td>
                                    <td className="px-3 py-2 text-right"><Input type="number" min={0} value={r.price ?? 0} onChange={(e) => updateRow(i, { price: Number(e.target.value) })} className="rounded-lg h-8 text-xs text-right w-24 ml-auto" data-testid={`variant-price-${i}`} /></td>
                                    <td className="px-3 py-2 text-right"><Input type="number" min={0} value={r.stock ?? 0} onChange={(e) => updateRow(i, { stock: Number(e.target.value) })} className="rounded-lg h-8 text-xs text-right w-20 ml-auto" data-testid={`variant-stock-${i}`} /></td>
                                    <td className="px-3 py-2 text-right">
                                        <Button type="button" size="icon" variant="outline" className="rounded-full h-7 w-7 text-accent-700 hover:bg-accent-50" onClick={() => removeRow(i)} data-testid={`variant-remove-${i}`}><Trash2 className="h-3 w-3" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
